import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useTranslate } from "utils/i18n";
import {
  deleteTeacherPermanently,
  deletionErrorDetails,
  fetchTeacherDeletionImpact,
} from "services/teacherDeletionService";

const COUNT_LABELS = {
  assignments: "Assignments",
  attendance: "Attendance records",
  leaveRequests: "Leave requests",
  leaveBalances: "Leave balances",
  documents: "Documents",
  sessions: "Sessions",
  otpRecords: "OTP records",
  authorizedMovements: "Authorized movements",
  notifications: "Notifications",
  ussdRecords: "USSD records",
  legacyRecords: "Legacy records",
};

export function isTeacherDeletionReady(impact, reason, confirmation) {
  return Boolean(
    impact?.canDelete &&
      String(reason ?? "").trim() &&
      confirmation === impact.requiredConfirmation
  );
}

function TeacherDeletionDialog({ open, teacherId, onClose, onDeleted }) {
  const { t } = useTranslate();
  const [impact, setImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !teacherId) return undefined;
    const controller = new AbortController();
    setImpact(null);
    setReason("");
    setConfirmation("");
    setError(null);
    setLoadingImpact(true);
    fetchTeacherDeletionImpact(teacherId, { signal: controller.signal })
      .then(setImpact)
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          setError(deletionErrorDetails(err, t("teachers.delete.impact_error", "Unable to inspect deletion impact.")));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingImpact(false);
      });
    return () => controller.abort();
  }, [open, teacherId, t]);

  const countEntries = useMemo(
    () => Object.entries(impact?.counts ?? {}).filter(([, value]) => Number(value) > 0),
    [impact]
  );
  const ready = isTeacherDeletionReady(impact, reason, confirmation);

  const handleDelete = async () => {
    if (!ready || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await deleteTeacherPermanently(teacherId, { reason, confirmation });
      await onDeleted(result, impact);
    } catch (err) {
      setError(deletionErrorDetails(err));
    } finally {
      setDeleting(false);
    }
  };

  const controlsDisabled = loadingImpact || deleting;

  return (
    <Dialog open={open} onClose={controlsDisabled ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "error.main", fontWeight: 800 }}>
        {t("teachers.delete.title", "Permanently delete teacher")}
      </DialogTitle>
      <DialogContent>
        {loadingImpact ? (
          <SoftBox py={5} display="flex" justifyContent="center"><CircularProgress color="error" /></SoftBox>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{error.message}</strong>
            {error.code ? <div>{error.code}</div> : null}
            {error.fieldErrors?.map((message) => <div key={message}>{message}</div>)}
          </Alert>
        ) : null}

        {impact ? (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("teachers.delete.irreversible", "This operation cannot be undone. All related records listed below will be permanently deleted.")}
            </Alert>
            <SoftTypography variant="h6" fontWeight="bold">
              {[impact.name, impact.preName].filter(Boolean).join(" ") || "—"}
            </SoftTypography>
            <SoftTypography variant="body2">{t("teachers.table.reg", "Matricule")}: {impact.regNumber || "—"}</SoftTypography>
            <SoftTypography variant="body2">{t("teachers.table.phone", "Phone")}: {impact.phone || "—"}</SoftTypography>
            <SoftTypography variant="body2" mb={1.5}>{t("teachers.table.status", "Status")}: {impact.status || "—"}</SoftTypography>
            <Divider />
            <List dense disablePadding sx={{ my: 1 }}>
              {countEntries.length ? countEntries.map(([key, value]) => (
                <ListItem key={key} disableGutters sx={{ justifyContent: "space-between" }}>
                  <SoftTypography variant="body2">{COUNT_LABELS[key] ?? key}</SoftTypography>
                  <SoftTypography variant="body2" fontWeight="bold">{value}</SoftTypography>
                </ListItem>
              )) : <ListItem disableGutters>{t("teachers.delete.no_related", "No related records were reported.")}</ListItem>}
            </List>
            {impact.blockingReasons?.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {impact.blockingReasons.map((reasonText) => <div key={reasonText}>{reasonText}</div>)}
              </Alert>
            ) : null}
            <TextField
              fullWidth multiline minRows={2} margin="normal"
              label={t("teachers.delete.reason", "Deletion reason")}
              value={reason} onChange={(event) => setReason(event.target.value)}
              disabled={controlsDisabled} required
            />
            <SoftTypography variant="caption" display="block" mt={1}>
              {t("teachers.delete.type_confirmation", "Type exactly")}: <strong>{impact.requiredConfirmation}</strong>
            </SoftTypography>
            <TextField
              fullWidth margin="dense" value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={controlsDisabled} inputProps={{ "aria-label": "Deletion confirmation" }}
              error={Boolean(confirmation && confirmation !== impact.requiredConfirmation)}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <SoftButton variant="outlined" color="secondary" onClick={onClose} disabled={controlsDisabled}>
          {t("common.cancel", "Cancel")}
        </SoftButton>
        <SoftButton variant="gradient" color="error" onClick={handleDelete} disabled={!ready || controlsDisabled}>
          {deleting ? t("teachers.delete.deleting", "Deleting…") : t("teachers.delete.confirm", "Delete permanently")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

TeacherDeletionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  teacherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default TeacherDeletionDialog;
